/**
 * MODULE: _shared/frontend-shell/src/components/assistant/AssistantLearningCatchUpBanner.tsx
 * PURPOSE: Inline composer banner when post-chat catch-up detects self-learning progress [2.2.1231].
 * DEPENDENCIES: flowbite-react, @heroicons/react, lucide-react
 * PUBLIC_API: AssistantLearningCatchUpBanner, AssistantLearningCatchUpBannerProps
 * SIDE_EFFECTS: None (presentational)
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 67 / 200 lines
 * RECENT: 2026-05-26 [2.2.1231] — Initial release; pairs with useAssistantContext learningCatchUpHint.
 */

import { Button } from 'flowbite-react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Brain } from 'lucide-react';

export type AssistantLearningCatchUpBannerProps = {
  hint: string;
  onViewContext?: () => void;
  onDismiss?: () => void;
  compact?: boolean;
};

export function AssistantLearningCatchUpBanner({
  hint,
  onViewContext,
  onDismiss,
  compact = false,
}: AssistantLearningCatchUpBannerProps) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50/95 text-violet-950 dark:border-violet-900/50 dark:bg-violet-950/40 dark:text-violet-100 ${
        compact ? 'px-2.5 py-2 text-xs' : 'px-3 py-2.5 text-sm'
      }`}
      role="status"
      aria-live="polite"
      data-testid="assistant-learning-catchup-banner"
    >
      <Brain
        className={`mt-0.5 shrink-0 text-violet-600 dark:text-violet-400 ${compact ? 'h-4 w-4' : 'h-5 w-5'}`}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-violet-900 dark:text-violet-100">Brazn learned from this chat</p>
        <p className="mt-0.5 text-violet-800/95 dark:text-violet-200/95">{hint}</p>
        {onViewContext ? (
          <Button
            size="xs"
            color="light"
            className="mt-2 border-violet-200 bg-white/90 text-violet-800 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-100"
            data-testid="assistant-learning-catchup-view-context"
            onClick={onViewContext}
          >
            View learning status
          </Button>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          className="shrink-0 rounded-md p-1 text-violet-600 hover:bg-violet-100 dark:text-violet-300 dark:hover:bg-violet-900/60"
          aria-label="Dismiss learning update"
          data-testid="assistant-learning-catchup-dismiss"
          onClick={onDismiss}
        >
          <XMarkIcon className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
